import React, {
  forwardRef,
  PropsWithChildren,
  ReactNode,
  Ref,
  useState,
  useMemo,
  useEffect,
  useImperativeHandle,
} from "react";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import Skeleton from "@material-ui/lab/Skeleton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Collapse from "@material-ui/core/Collapse";
import { makeStyles } from "@material-ui/styles";

import useGqlTableStyles from "./gql_table.style";
import { useSnackbarQuery } from "../../../hooks";

const useCollapsibleRowStyles = makeStyles({
  root: {
    "& > *": {
      borderBottom: "unset",
    },
  },
});

interface CollapsibleRowProps {
  id: string;
  row: ReactNode;
  collapsibleContent: ReactNode;
  colSpan: number;
}
function CollapsibleRow(props: CollapsibleRowProps) {
  const { id, row, colSpan, collapsibleContent } = props;
  const [open, setOpen] = useState(false);

  const classes = useCollapsibleRowStyles();

  return (
    <>
      <TableRow className={classes.root} key={id}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        {row}
      </TableRow>
      <TableRow key={`${id}-collapse-content`}>
        <TableCell colSpan={colSpan}>
          <Collapse in={open}>{collapsibleContent}</Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export interface GqlTableProps extends PropsWithChildren<{}> {
  query: any;
  queryVariables?: any;
  queryResultDataField: string;
  autoRefreshTimeoutInSeconds?: number;
  columns: ReactNode[];
  rows: (row: any) => ReactNode;
  collapseContent?: (row: any) => ReactNode;
  sortFn?: (a: any, b: any) => number;
}

const GqlTable = forwardRef((props: GqlTableProps, ref: Ref<any>) => {
  const {
    query,
    queryVariables,
    queryResultDataField,
    autoRefreshTimeoutInSeconds = 60,
    columns,
    rows,
    collapseContent,
    sortFn,
  } = props;

  const computedColumns = useMemo(() => {
    if (collapseContent) {
      return [
        <TableCell key="first-col" style={{ width: "1%" }} />,
        ...columns,
      ];
    }

    return columns;
  }, [columns, collapseContent]);

  const classes = useGqlTableStyles();

  const { loading, data, error, refetch, startPolling, stopPolling } =
    useSnackbarQuery(query, {
      variables: queryVariables,
      fetchPolicy: "network-only",
    });

  useEffect(() => {
    if (startPolling && autoRefreshTimeoutInSeconds) {
      startPolling(autoRefreshTimeoutInSeconds * 1000);
    }
    
    // Destructor
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling, autoRefreshTimeoutInSeconds]);

  const queryData = useMemo(() => {
    if (data && queryResultDataField) {
      return data[queryResultDataField];
    }
    return [];
  }, [data, queryResultDataField]);

  useImperativeHandle(ref, () => ({
    refresh: () => refetch(),
  }));

  if (loading) {
    return <Skeleton animation="wave" />;
  }
  if (error) {
    return <span>Si è verificato un errore, contattare l'assistenza.</span>;
  }

  return (
    <TableContainer className={classes.root} component={Paper}>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow className={classes.tableHead}>{computedColumns}</TableRow>
        </TableHead>
        <TableBody>
          {queryData &&
            (sortFn ? [...queryData].sort(sortFn) : queryData).map(
              (row, index) => {
                const cells = rows(row);

                if (collapseContent) {
                  return (
                    <CollapsibleRow
                      key={index}
                      id={row.id}
                      row={cells}
                      collapsibleContent={collapseContent(row)}
                      colSpan={computedColumns.length}
                    />
                  );
                }
                return <TableRow key={index}>{cells}</TableRow>;
              }
            )}
          {Array.isArray(queryData) && queryData.length === 0 && (
            <TableRow>
              <TableCell
                className={classes.noResultsRow}
                colSpan={columns.length}
              >
                - nessun risultato -
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

export { GqlTable };
